import { TrendingUp, TrendingDown, Minus, FolderKanban, Clock, FileCheck, Wallet } from 'lucide-react'

import { cn } from '@/lib/utils'

import type { DashboardStatistics } from '@/hooks'

interface StatisticsCardsProps {
  statistics: DashboardStatistics | null
  isLoading?: boolean
}

interface StatCardProps {
  title: string
  value: string | number
  change?: number
  icon: React.ReactNode
  description?: string
  iconColor: string
  iconBgColor: string
}

function StatCard({ title, value, change, icon, description, iconColor, iconBgColor }: StatCardProps) {
  const getTrendIcon = () => {
    if (change === undefined || change === 0) {
      return <Minus className="h-3 w-3 text-muted-foreground" />
    }
    return change > 0 ? (
      <TrendingUp className="h-3 w-3 text-green-500" />
    ) : (
      <TrendingDown className="h-3 w-3 text-red-500" />
    )
  }

  const getTrendColor = () => {
    if (change === undefined || change === 0) {return 'text-muted-foreground'}
    return change > 0 ? 'text-green-500' : 'text-red-500'
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <div className={cn('rounded-full p-2', iconBgColor)}>
          <div className={iconColor}>{icon}</div>
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {(change !== undefined || description) && (
        <div className={cn('flex items-center gap-1 text-xs mt-1', getTrendColor())}>
          {change !== undefined && (
            <>
              {getTrendIcon()}
              <span>{change > 0 ? '+' : ''}{change}%</span>
            </>
          )}
          {description && <span className="text-muted-foreground ml-1">{description}</span>}
        </div>
      )}
    </div>
  )
}

function StatCardSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-4 w-20 bg-muted rounded" />
        <div className="h-8 w-8 bg-muted rounded-full" />
      </div>
      <div className="mt-4 h-8 w-16 bg-muted rounded" />
      <div className="mt-2 h-3 w-24 bg-muted rounded" />
    </div>
  )
}

export function StatisticsCards({ statistics, isLoading }: StatisticsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
    )
  }

  if (!statistics) {
    return null
  }

  const formatCurrency = (cents: number) => {
    return `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="活跃项目"
        value={statistics.activeProjects.count}
        change={statistics.activeProjects.change}
        icon={<FolderKanban className="h-4 w-4" />}
        description="较上月"
        iconColor="text-blue-500"
        iconBgColor="bg-blue-50"
      />
      <StatCard
        title="本周工时"
        value={`${statistics.weeklyHours.total}h`}
        change={statistics.weeklyHours.change}
        icon={<Clock className="h-4 w-4" />}
        description="较上周"
        iconColor="text-green-500"
        iconBgColor="bg-green-50"
      />
      <StatCard
        title="待审批"
        value={statistics.pendingApprovals.count}
        icon={<FileCheck className="h-4 w-4" />}
        description={`请假 ${statistics.pendingApprovals.leaves} / 报销 ${statistics.pendingApprovals.reimbursements}`}
        iconColor="text-orange-500"
        iconBgColor="bg-orange-50"
      />
      {statistics.monthlyCashFlow && (
        <StatCard
          title="本月净额"
          value={formatCurrency(statistics.monthlyCashFlow.net)}
          change={statistics.monthlyCashFlow.change}
          icon={<Wallet className="h-4 w-4" />}
          description="较上月"
          iconColor="text-purple-500"
          iconBgColor="bg-purple-50"
        />
      )}
    </div>
  )
}
